import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Group as CommunityIcon, Search as SearchIcon } from '@mui/icons-material';
import CommunityEditForm from './CommunityEditForm';
import CommunityService from '../../services/community-service';
import { useRoleCheck } from '../../hooks/use-role-check';
import EditPageLayout from '../common/EditPageLayout';

interface CommunityData {
  recordID: string;
  name?: string;
}

interface CommunitySearchSectionProps {
  recordId: string;
  onRecordIdChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
  onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  loading: boolean;
}

const CommunitySearchSection = ({
  recordId,
  onRecordIdChange,
  onSearch,
  onKeyPress,
  loading,
}: CommunitySearchSectionProps): React.JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      marginBottom: 2,
      '@media (min-width: 960px)': {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        marginBottom: 3,
      },
    }}
  >
    <TextField
      sx={{ flexGrow: 1 }}
      label="Community Record ID"
      variant="outlined"
      value={recordId}
      onChange={onRecordIdChange}
      onKeyPress={onKeyPress}
      placeholder="Enter community record ID..."
      disabled={loading}
      helperText="Enter the unique Record ID of the community you want to edit"
    />
    <Button
      variant="contained"
      color="primary"
      onClick={onSearch}
      disabled={loading || !recordId.trim()}
      startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
    >
      {loading ? 'Searching...' : 'Search'}
    </Button>
  </Box>
);

const CommunityEditPage = (): React.JSX.Element => {
  const { communityId } = useParams<{ communityId?: string }>();
  const [recordId, setRecordId] = useState(communityId ?? '');
  const [community, setCommunity] = useState<CommunityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);

  const { isAdmin, isLoading: roleLoading, error: roleError } = useRoleCheck();

  const handleRecordIdChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRecordId(event.target.value);
  };

  const handleSearchCommunity = async (): Promise<void> => {
    if (!recordId.trim()) {
      setError('Please enter a Record ID');
      return;
    }

    setLoading(true);
    setError(null);
    setShowEditForm(false);

    try {
      const communityService = new CommunityService();
      const communityData = (await communityService.getCommunityById(
        recordId.trim(),
      )) as CommunityData | null;

      if (communityData) {
        setCommunity(communityData);
        setShowEditForm(true);
        setError(null);
      } else {
        setCommunity(null);
        setShowEditForm(false);
        setError(`Community with Record ID "${recordId}" not found`);
      }
    } catch (err) {
      console.error('Error loading community:', err);
      setCommunity(null);
      setShowEditForm(false);
      const message = err instanceof Error ? err.message : String(err);
      setError(`Failed to load community: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCommunityUpdate = (updatedCommunity: unknown): void => {
    setCommunity(updatedCommunity as CommunityData);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      void handleSearchCommunity();
    }
  };

  const getErrorMessage = (): string | null => {
    if (roleError)
      return 'Failed to verify your permissions. Please try refreshing the page.';
    if (!roleLoading && !isAdmin())
      return 'You must have admin privileges to access the community edit feature.';
    if (error) return error;
    return null;
  };

  const getLoadingState = (): boolean => roleLoading || loading;

  return (
    <EditPageLayout
      title="Community Edit"
      titleIcon={<CommunityIcon />}
      subtitle="Enter a Record ID to search for and edit a community record."
      isLoading={getLoadingState()}
      error={getErrorMessage()}
    >
      {!roleLoading && isAdmin() && (
        <>
          <CommunitySearchSection
            recordId={recordId}
            onRecordIdChange={handleRecordIdChange}
            onSearch={handleSearchCommunity}
            onKeyPress={handleKeyPress}
            loading={loading}
          />

          {error && !roleError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error}
            </Alert>
          )}

          {showEditForm && community && (
            <Box
              sx={{
                marginTop: 2,
                '@media (min-width: 960px)': { marginTop: 3 },
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: '1.25rem',
                  '@media (min-width: 960px)': { fontSize: '1.5rem' },
                }}
              >
                Editing: {community.name ?? 'Unnamed Community'}
              </Typography>
              <CommunityEditForm
                community={community as unknown as React.ComponentProps<typeof CommunityEditForm>['community']}
                onCommunityUpdate={handleCommunityUpdate}
              />
            </Box>
          )}
        </>
      )}
    </EditPageLayout>
  );
};

export default CommunityEditPage;
